// 파일 전송(설계서 §9.4) — **받은 파일을 둘 곳**을 한 인터페이스로 감싼다.
//
//  - 브라우저: 사용자가 고른 폴더(`showDirectoryPicker`, Chromium 계열) — 스트리밍으로 써서 10 GiB 도 메모리에 올리지 않는다.
//  - 통합앱: 네이티브 스테이징 → 내 파일 클립보드(앱 쪽 구현 — 같은 FileSink 인터페이스).
// 채널은 조각을 **순서대로** 넘긴다(위치 검사는 채널이 한다).
import { FILE_ERR, FileError, type Entry } from './protocol';

export interface FileSink {
  /** 전송 시작 — 폴더를 만들고 맨 위 이름 충돌을 피한다. */
  begin(entries: Entry[]): Promise<void>;
  /** 파일 `i` 에 다음 조각을 붙인다(채널이 순서를 보장한다). 일반 ArrayBuffer 기반이어야 한다(파일 쓰기 API 요구). */
  write(i: number, data: Uint8Array<ArrayBuffer>): Promise<void>;
  /** 파일 `i` 끝(해시 확인은 채널이 했다) — 닫는다. */
  finishFile(i: number): Promise<void>;
  /** 전체 끝. */
  complete(): Promise<void>;
  /** 취소·실패 — 만든 것을 지운다. */
  abort(): Promise<void>;
  /** 알림에 쓸 저장 위치 설명(예: 고른 폴더 이름). */
  readonly label: string;
}

type DirPicker = (opts?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>;

/** 받은 조각을 모았다가 한 번에 쓰는 크기. */
const FLUSH_BYTES = 1024 * 1024;

/** 이 브라우저가 폴더에 저장할 수 있는가(Chromium 계열). */
export function canPickDirectory(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: DirPicker }).showDirectoryPicker === 'function';
}

/** 폴더 고르기 — **사용자 동작(클릭) 안에서** 불러야 한다(브라우저 규칙). */
export async function pickDirectory(): Promise<FileSystemDirectoryHandle> {
  const picker = (window as unknown as { showDirectoryPicker?: DirPicker }).showDirectoryPicker;
  if (!picker) {
    throw new FileError(FILE_ERR.NO_SAVE_API);
  }
  return picker({ mode: 'readwrite' });
}

/** 고른 폴더에 스트리밍으로 쓴다. */
export class DirectorySink implements FileSink {
  private entries: Entry[] = [];
  /** 맨 위 이름 → 실제로 쓸 이름(충돌 시 "이름 (2)"). */
  private readonly topRename = new Map<string, string>();
  private readonly dirs = new Map<string, FileSystemDirectoryHandle>();
  private readonly writers = new Map<number, FileSystemWritableFileStream>();
  /** 아직 쓰지 않은 조각(파일별). */
  private readonly pending = new Map<number, { parts: Uint8Array<ArrayBuffer>[]; size: number }>();
  private readonly createdTops = new Set<string>();

  constructor(private readonly root: FileSystemDirectoryHandle) {}

  get label(): string {
    return this.root.name;
  }

  async begin(entries: Entry[]): Promise<void> {
    this.entries = entries;
    for (const top of new Set(entries.map((e) => e.path.split('/')[0]))) {
      this.topRename.set(top, await this.freeName(top));
    }
    for (const e of entries) {
      if (e.dir) {
        await this.dirOf(e.path, true);
      }
    }
  }

  async write(i: number, data: Uint8Array<ArrayBuffer>): Promise<void> {
    // 조각(32KB)마다 쓰지 않고 FLUSH_BYTES 만큼 모아서 쓴다 — 쓰기 한 번의 부담이 속도를 좌우한다.
    let p = this.pending.get(i);
    if (!p) {
      p = { parts: [], size: 0 };
      this.pending.set(i, p);
    }
    p.parts.push(data);
    p.size += data.length;
    if (p.size >= FLUSH_BYTES) {
      await this.flush(i);
    }
  }

  /** 모아 둔 조각을 한 번에 쓴다. */
  private async flush(i: number): Promise<void> {
    const p = this.pending.get(i);
    if (!p || p.size === 0) {
      return;
    }
    const merged = new Uint8Array(p.size);
    let at = 0;
    for (const part of p.parts) {
      merged.set(part, at);
      at += part.length;
    }
    p.parts = [];
    p.size = 0;
    try {
      let w = this.writers.get(i);
      if (!w) {
        const e = this.entries[i];
        const parts = this.mapped(e.path);
        const dir = await this.dirOf(parts.slice(0, -1).join('/'), false);
        const fh = await dir.getFileHandle(parts[parts.length - 1], { create: true });
        w = await fh.createWritable();
        this.writers.set(i, w);
      }
      await w.write(merged);
    } catch (e) {
      throw e instanceof FileError ? e : new FileError(FILE_ERR.LOCAL_WRITE, String(e));
    }
  }

  async finishFile(i: number): Promise<void> {
    await this.flush(i);
    this.pending.delete(i);
    const w = this.writers.get(i);
    if (w) {
      this.writers.delete(i);
      await w.close();
      return;
    }
    // 빈 파일 — 조각이 오지 않았다. 만들기만 한다.
    const e = this.entries[i];
    if (e && !e.dir) {
      const parts = this.mapped(e.path);
      const dir = await this.dirOf(parts.slice(0, -1).join('/'), false);
      const fh = await dir.getFileHandle(parts[parts.length - 1], { create: true });
      const empty = await fh.createWritable();
      await empty.close();
    }
  }

  async complete(): Promise<void> {
    for (const i of [...this.writers.keys()]) {
      await this.finishFile(i);
    }
  }

  async abort(): Promise<void> {
    for (const w of this.writers.values()) {
      await w.abort().catch(() => undefined);
    }
    this.writers.clear();
    for (const top of this.createdTops) {
      await this.root.removeEntry(top, { recursive: true }).catch(() => undefined);
    }
  }

  /** 맨 위 이름 충돌을 피한 경로 구성 요소. */
  private mapped(path: string): string[] {
    const parts = path.split('/');
    parts[0] = this.topRename.get(parts[0]) ?? parts[0];
    return parts;
  }

  /** 상대 폴더 경로(맨 위 이름 바꾼 뒤) → 핸들. `rel` 은 원래 경로(바꾸기 전). */
  private async dirOf(rel: string, fromEntry: boolean): Promise<FileSystemDirectoryHandle> {
    const parts = rel ? (fromEntry ? this.mapped(rel) : rel.split('/')) : [];
    let cur = this.root;
    let key = '';
    for (const c of parts) {
      key = key ? `${key}/${c}` : c;
      const hit = this.dirs.get(key);
      if (hit) {
        cur = hit;
        continue;
      }
      cur = await cur.getDirectoryHandle(c, { create: true });
      this.dirs.set(key, cur);
      if (!key.includes('/')) {
        this.createdTops.add(c);
      }
    }
    return cur;
  }

  /** 고른 폴더에 같은 이름이 있으면 "이름 (2).확장자" 식으로 비어 있는 이름을 찾는다(덮어쓰지 않는다). */
  private async freeName(name: string): Promise<string> {
    const dot = name.lastIndexOf('.');
    const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
    for (let n = 1; n < 1000; n++) {
      const candidate = n === 1 ? name : `${stem} (${n})${ext}`;
      if (!(await this.exists(candidate))) {
        if (candidate !== name) {
          this.createdTops.add(candidate);
        } else {
          this.createdTops.add(name);
        }
        return candidate;
      }
    }
    throw new FileError(FILE_ERR.LOCAL_WRITE, '빈 이름을 찾지 못함');
  }

  private async exists(name: string): Promise<boolean> {
    try {
      await this.root.getFileHandle(name);
      return true;
    } catch {
      try {
        await this.root.getDirectoryHandle(name);
        return true;
      } catch {
        return false;
      }
    }
  }
}
